/**
 * The collision screen.
 *
 * `docs/SOCIAL_LOGIN.md` § Account linking calls this "the dangerous case": an unknown provider
 * identity whose email matches an account we already hold. Linking it automatically without both
 * sides asserting a verified address is account takeover with extra steps, so the server refuses
 * and sends the person here.
 *
 * It is a screen, not a toast, because it has three things to say and a toast can hold one:
 * what happened, how to get in now, and how to make the button work next time.
 */

import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'

import { useT, useTNode } from '../../../lib/i18n'
import { Button } from '../../../components/ui/Button'
import { Icon } from '../../../components/ui/Icon'
import { AuthLayout } from '../AuthLayout'
import { emailInUseCopy } from './copy'
import type { SocialCopyContext } from './copy'

export interface EmailInUseScreenProps {
  ctx: SocialCopyContext
  /** The address the provider asserted, when the callback passed it back. */
  email: string | null
  /** Returns to the password form on this card, with the address already filled in. */
  onUsePassword: () => void
}

export const EmailInUseScreen = ({ ctx, email, onUsePassword }: EmailInUseScreenProps): ReactElement => {
  const t = useT()
  const tn = useTNode()
  const copy = emailInUseCopy(t, ctx)

  return (
    <AuthLayout
      title={copy.title}
      subtitle={copy.body}
      footer={
        <p className="t-small text-ink-mute">
          {tn('auth.social.forgottenPassword', {
            reset: (chunk) => (
              <Link
                to="/forgot"
                className="ring-focus font-semibold text-brass-deep underline underline-offset-2"
              >
                {chunk}
              </Link>
            ),
          })}
        </p>
      }
    >
      <div className="flex flex-col gap-5">
        {email ? (
          <div className="flex items-center gap-3 rounded-md border border-hair bg-surface p-3">
            <Icon name="user" size={18} className="shrink-0 text-ink-faint" />
            <span className="t-mono min-w-0 truncate text-ink" title={email}>
              {email}
            </span>
          </div>
        ) : null}

        <Button variant="primary" size="lg" fullWidth onClick={onUsePassword}>
          {t('auth.social.useYourPassword')}
        </Button>

        <div className="flex items-start gap-3 rounded-md border border-hair bg-paper p-4">
          <Icon name="link" size={18} className="mt-0.5 shrink-0 text-info" />
          <p className="t-small text-ink-mute">{copy.afterwards}</p>
        </div>
      </div>
    </AuthLayout>
  )
}
